import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useIsMounted from "../../hooks/useIsMounted";
import TextInput from "@components/forms/TextInput";
import { useAuth } from "@contexts/authContext";
import Button from "@components/ui/Button";
import { Link, useNavigate, useSearchParams } from "react-router";
import AlertBox from "@components/ui/AlertBox";
import { toast } from "sonner";
import PasswordInput from "../../components/forms/PasswordInput";
import GoogleLoginButton from "../../components/ui/GoogleLoginButton";
import { useRedirectParam } from "../../hooks/useRedirectParam";

function LoginPage() {
  let navigate = useNavigate();
  let [searchParams, _] = useSearchParams();
  const { consumeRedirect } = useRedirectParam();
  let {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  let { user, login } = useAuth();
  const [pageError, setPageError] = useState("");
  const isMounted = useIsMounted();
  const isSubmittingLoginRef = useRef(false);

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (isMounted.current && user && !isSubmittingLoginRef.current) {
      toast.info("You're already logged in");
      
      const storedRedirect = consumeRedirect();
      const redirectTo = searchParams.get("redirect");
      const hasSafeRedirect =
        redirectTo?.startsWith("/") && !redirectTo.startsWith("//");
      
      // Redirect based on user type
      if (storedRedirect) {
        navigate(storedRedirect, { replace: true });
      } else if (hasSafeRedirect) {
        navigate(redirectTo, { replace: true });
      } else if (user.is_admin) {
        navigate("/admin/", { replace: true });
      } else if (!user.is_profile_complete) {
        navigate("/complete-profile/role-selection", { replace: true });
      } else if (user.user_type) {
        navigate(`/${user.user_type}/dashboard`, { replace: true });
      }
    }
  }, [user, navigate, isMounted, consumeRedirect, searchParams]);

  let onSubmit = async (data) => {
    // clear error
    setPageError("");
    try {
      isSubmittingLoginRef.current = true;
      const result = await login(data);

      // Case B: Direct login when OTP is disabled
      if (result?.otp_required === false) {
        toast.success(result.message || "Login successful");
        const loggedUser = result.user;
        const storedRedirect = consumeRedirect();
        const redirectTo = searchParams.get("redirect");
        const hasSafeRedirect =
          redirectTo?.startsWith("/") && !redirectTo.startsWith("//");

        if (storedRedirect) {
          navigate(storedRedirect, { replace: true });
        } else if (hasSafeRedirect) {
          navigate(redirectTo, { replace: true });
        } else if (loggedUser?.is_admin) {
          navigate("/admin/", { replace: true });
        } else if (!loggedUser?.is_profile_complete) {
          navigate("/complete-profile/role-selection", { replace: true });
        } else if (loggedUser?.user_type) {
          navigate(`/${loggedUser.user_type}/dashboard`, { replace: true });
        } else {
          navigate("/student/dashboard", { replace: true });
        }
        return;
      }

      // Case A: OTP is required
      isSubmittingLoginRef.current = false;
      toast.success(result?.message || "OTP sent to your email");
      // Store email in sessionStorage for OTP verification
      sessionStorage.setItem("otpEmail", result?.email || data.email);
      const redirectTo = searchParams.get("redirect");
      const otpPath = redirectTo
        ? `/verify-otp?redirect=${encodeURIComponent(redirectTo)}`
        : "/verify-otp";

      navigate(otpPath, { replace: true });
    } catch (error) {
      isSubmittingLoginRef.current = false;
      console.log("from component", error);
      setPageError(error);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-[calc(100vh-100px)]">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8 mt-2">
          <h1 className="text-3xl font-bold">Welcome Back!</h1>
          <p className="mt-3 text-base-content/70">Please sign in to your account</p>
        </div>

        <div className="card bg-base-100 shadow-xl">
          <form onSubmit={handleSubmit(onSubmit)} className="card-body">
            <AlertBox message={pageError} type="error" />

            <TextInput
              label="Email"
              name="email"
              type="email"
              placeholder="john.doe@example.com"
              register={register}
              errors={errors}
              validationRules={{ required: "Email is required" }}
            />

            <div>
              <PasswordInput
                label="Password"
                name="password"
                register={register}
                errors={errors}
                placeholder="********"
                validationRules={{ required: "Password is required" }}
              />
              <Link className="text-xs float-end hover:underline" to={"/request-reset-password"}>
                Forgot Password?
              </Link>
            </div>

            <Button isLoading={isSubmitting} className="w-full mt-4">
              Login
            </Button>

            {/* Divider */}
            <div className="divider text-sm">OR</div>

            {/* Google Login Button */}
            <GoogleLoginButton />

            {/* Sign Up Link */}
            <div className="text-center text-sm">
              Don't have an account?{" "}
              <Link
                to={searchParams.get("redirect") ? `/signup?redirect=${encodeURIComponent(searchParams.get("redirect"))}` : "/signup"}
                className="link link-primary"
              >
                Sign up
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
